import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import EventsListV2 from './EventsListV2';
import EventsListSkeletonV2 from './EventsListSkeletonV2';
import { selectEvents } from 'flow/selectors/TicketEventSelectors';
import {
    fetchTicketEvents,
    setTicketEventsMountStatus,
} from 'flow/actions/tickets-action';
import { TicketKey } from 'flow/types/EntityKeys';

interface Props {
    ticketKey: TicketKey;
    discardCallEvents?: boolean;
    discriminatorDate?: string;
    dateLabelBgClassName?: string;
    // False while the list is kept mounted but hidden, e.g. behind a side tab
    // on the ticket page, so its scroll position survives.
    visible?: boolean;
}

const TicketEventsListWrapperV2 = ({
    ticketKey,
    discardCallEvents,
    discriminatorDate,
    dateLabelBgClassName,
    visible = true,
}: Props) => {
    const dispatch = useAppDispatch();
    const ticketEvents = useAppSelector((state) =>
        selectEvents(state, ticketKey),
    );

    // The mount status means "this ticket's events are on screen": it
    // suppresses the ticket's notifications and makes pushes update the feed
    // in place. A hidden list therefore clears it, so notifications fire as if
    // it were unmounted, and refetches when shown again to pick up what those
    // pushes left out of the store meanwhile (e.g. email messages).
    useEffect(() => {
        if (!visible) return;
        dispatch(fetchTicketEvents(ticketKey));
    }, [dispatch, ticketKey, visible]);

    useEffect(() => {
        if (!visible) return undefined;
        dispatch(setTicketEventsMountStatus(ticketKey, true));
        return () => {
            dispatch(setTicketEventsMountStatus(ticketKey, false));
        };
    }, [dispatch, ticketKey, visible]);

    if (!ticketEvents) {
        return (
            <EventsListSkeletonV2
                ticketKey={ticketKey}
                dateLabelBgClassName={dateLabelBgClassName}
            />
        );
    }

    return (
        <EventsListV2
            ticketKey={ticketKey}
            ticketEvents={ticketEvents}
            discardCallEvents={discardCallEvents}
            discriminatorDate={discriminatorDate}
            dateLabelBgClassName={dateLabelBgClassName}
        />
    );
};

export default React.memo(TicketEventsListWrapperV2);
